import {
  Menu,
  NavBar as SdsNavBar,
} from "@sede-enterprise/shell-ds-react-framework";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  getUserManagementPermissions,
  getGenerateReportPermissions,
} from "../../utils/permissions";
import {
  canAccessMpVapFeatures,
  canAccessPlusCardFeatures,
  canAccessLandingPage,
  getAccessibleMasterDataKeys,
} from "../../utils/userTypePermissions";
import HomeSolid from "../../assets/icons/HomeSolid";
import LineChartSolid from "../../assets/icons/LineChartSolid";
import CoinEuro from "../../assets/icons/CoinEuro";
import PeopleSolid from "../../assets/icons/PeopleSolid";
import DatabaseBase from "../../assets/icons/DataBase";
import LogOutIcon from "../../assets/icons/LogOutIcon";
import LanguageSwitcherComponent from "../LanguageSwitcher/LanguageSwitcher";
import { CreditCard } from "@mui/icons-material";
import { clearUserInfo } from "../../store/slices/user/userSlice";
import Badge from "@mui/icons-material/Badge";

const NavBar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Get user info from Redux store (populated by App.tsx)
  const userInfo = useAppSelector((state) => state.user);

  // Normalize pathname for navigation selection
  // This ensures parent nav items are highlighted for their child routes
  const getSelectedPath = (path: string) => {
    if (path.startsWith("/reports/access/")) {
      return "/reports/access/declaration"; // Match any access report route
    }
    if (path.startsWith("/site-miscellaneous/")) {
      return "/site-miscellaneous/vat-rates"; // Match any site miscellaneous route
    }
    if (path.startsWith("/contract-parameters/")) {
      return "/contract-parameters/partner";
    }
    if (path.startsWith("/gsap-volumes/")) {
      return "/gsap-volumes/volumes";
    }
    if (path.startsWith("/pricing/")) {
      return "/pricing/fuel-prices";
    }
    if (path.startsWith("/plus-card/cards/")) {
      return "/plus-card/cards/downloads"; // Highlight Card List for any cards sub-route
    }
    if (path.startsWith("/plus-card/transactions")) {
      return "/plus-card/transactions";
    }
    return path;
  };

  // Generate user display values with fallbacks
  const userName =
    userInfo?.firstName && userInfo?.lastName
      ? `${userInfo.firstName} ${userInfo.lastName}`
      : "User";
  const userEmail = userInfo?.email || "user@shell.com";
  const userInitials =
    userInfo && userInfo.firstName && userInfo.lastName
      ? `${userInfo.firstName.charAt(0)}${userInfo.lastName.charAt(0)}`.toUpperCase()
      : "U";

  // Check if user has user management view permission
  const canViewUserManagement = getUserManagementPermissions(
    userInfo?.roleId || 0,
  ).canView;

  // Check if user has generate report permission
  const canAccessGenerateReport = getGenerateReportPermissions(
    userInfo?.roleId || 0,
  ).canView;

  // Get user type for filtering menu items
  const userTypeName = userInfo?.userTypeName || "";

  // Check user type permissions
  const showLanding = canAccessLandingPage(userTypeName);
  const showMpVapFeatures = canAccessMpVapFeatures(userTypeName);
  const showPlusCardFeatures = canAccessPlusCardFeatures(userTypeName);
  const accessibleMasterDataKeys = getAccessibleMasterDataKeys(userTypeName);

  // Filter master data menu items based on user type
  const masterDataChildren = [
    {
      key: "1-1-1",
      label: t("navigation.clusters.masterSiteData"),
      path: "/mdm",
    },
    {
      key: "1-1-2",
      label: t("navigation.clusters.siteMiscellaneous"),
      path: "/site-miscellaneous/vat-rates",
    },
    {
      key: "1-1-3",
      label: t("navigation.clusters.partner"),
      path: "/partner",
    },
    {
      key: "1-1-4",
      label: t("navigation.clusters.regionData"),
      path: "/region-data",
    },
    {
      key: "1-1-5",
      label: t("navigation.clusters.productData"),
      path: "/product-data",
    },
  ].filter((item) => accessibleMasterDataKeys.includes(item.key));

  const navBarArgs = {
    urlProps: { href: "/landing" },
    header: {
      title: "OneSource",
    },
    preventExpand: false,
    itemsAlignment: "center" as const,
    endArea: <LanguageSwitcherComponent />,
    items: [
      // Home/Landing - only for MpVap and All users
      ...(showLanding
        ? [
            {
              label: t("navigation.landing"),
              itemIcon: <HomeSolid />,
              key: "1-0",
              path: "/landing",
            },
          ]
        : []),
      // Master Data - accessible by all user types but with filtered children
      {
        label: t("navigation.clusters.title"),
        itemIcon: <DatabaseBase />,
        key: "1-1",
        children: masterDataChildren,
      },
      // Pricing - only for MpVap and All users
      ...(showMpVapFeatures
        ? [
            {
              label: t("navigation.pricing.title"),
              itemIcon: <CoinEuro />,
              key: "1-2",
              children: [
                {
                  key: "1-2-1",
                  label: t("navigation.pricing.contractParameters"),
                  path: "/contract-parameters/partner",
                },
                {
                  key: "1-2-2",
                  label: t("navigation.pricing.gsapVolumes"),
                  path: "/gsap-volumes/volumes",
                },
                {
                  key: "1-2-3",
                  label: t("navigation.pricing.omrPrices"),
                  path: "/pricing/fuel-prices",
                },
                {
                  key: "1-2-4",
                  label: t("navigation.pricing.fuelDiscounts"),
                  path: "/fuel-discounts",
                },
                {
                  key: "1-2-5",
                  label: t("navigation.pricing.loyaltyDiscounts"),
                  path: "/loyalty-discounts",
                },
              ],
            },
          ]
        : []),
      // PlusCard - only for PlusCard and All users
      ...(showPlusCardFeatures
        ? [
            {
              label: t("navigation.plusCard.title"),
              itemIcon: <CreditCard />,
              key: "1-3",
              children: [
                {
                  key: "1-3-1",
                  label: t("navigation.plusCard.cardList"),
                  path: "/plus-card/cards/downloads",
                },
                {
                  key: "1-3-2",
                  label: t("navigation.plusCard.transactions"),
                  path: "/plus-card/transactions",
                },
              ],
            },
          ]
        : []),
      // Reports - only for MpVap and All users
      ...(showMpVapFeatures
        ? [
            {
              label: t("navigation.reports.title"),
              itemIcon: <LineChartSolid />,
              key: "1-4",
              children: [
                {
                  key: "1-4-1",
                  label: t("navigation.reports.accessReports"),
                  path: "/reports/access/declaration",
                },
                // Only show Generate Reports if user has permission
                ...(canAccessGenerateReport
                  ? [
                      {
                        key: "1-4-2",
                        label: t("navigation.reports.generateReports"),
                        path: "/reports/generate",
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
    ],
    avatarProps: {
      variant: "initials" as const,
      abbreviation: userInitials as Uppercase<string>,
      alt: userName,
      title: userName,
      label: userEmail,
      popoverContent: (
        <Menu
          items={[
            // Display user role - visible for all users
            {
              key: "role",
              label: (
                <>
                  <div style={{ fontStyle: "italic", fontSize: "0.875rem" }}>
                    {`Role: ${userInfo?.roleName || "Role"}, `}
                  </div>
                  <div style={{ fontStyle: "italic", fontSize: "0.875rem" }}>
                    {`User Type: ${userInfo?.userTypeName || "User Type"}`}
                  </div>
                </>
              ),
              disabled: true,
              itemIcon: <Badge />,
              style: { padding: "0px 16px" },
            },
            { type: "divider" as const },
            // Conditionally show User Management menu item based on permissions and user type
            // Visible for Admin, SuperUser, Write roles across all user types (including PlusCard)
            // Not visible for Read role
            ...(canViewUserManagement
              ? [
                  {
                    key: "admin",
                    label: t("navigation.userManagement"),
                    itemIcon: <PeopleSolid />,
                  },
                  { type: "divider" as const },
                ]
              : []),
            {
              key: "logout",
              label: t("common.logout"),
              itemIcon: <LogOutIcon />,
            },
          ]}
          onSelect={(event) => {
            if (event.key === "logout") {
              // Clear user info from Redux store
              dispatch(clearUserInfo());
              // Navigate first, then let the Logout page handle the actual logout
              void navigate("/logout");
            } else if (event.key === "admin") {
              void navigate("/admin");
            }
          }}
          style={{ minWidth: "12rem" }}
        />
      ),
    },
  };

  return (
    <SdsNavBar
      {...navBarArgs}
      className="navbar-custom"
      onSelect={(event) => {
        if (event.path) {
          void navigate(event.path);
        }
      }}
      selectedPath={getSelectedPath(pathname)}
    />
  );
};

export default NavBar;
